import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2, X, Globe, ExternalLink, Mail, Phone, Key, Eye, EyeOff,
  Calendar, Clock, Database, CheckCircle, Copy, RefreshCw, Send,
  User, CalendarPlus, Plus,
} from 'lucide-react';
import { ADMIN_API_URL, authFetch, formatDate } from './adminAuth';
import OrgStatusBadge from './OrgStatusBadge';

const BASE_DOMAIN = process.env.REACT_APP_BASE_DOMAIN || 'mawered.com';
const TENANT_SCHEME = process.env.REACT_APP_TENANT_SCHEME || 'https';

const tenantUrl = (subdomain, path = '') => `${TENANT_SCHEME}://${subdomain}.${BASE_DOMAIN}${path}`;

const OrganizationDetailPanel = ({ org, onClose, onRefresh }) => {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [selectedExtendDays, setSelectedExtendDays] = useState(null);
  const [extendResult, setExtendResult] = useState(null);

  const handleResetPassword = async () => {
    if (!window.confirm(t("orgDetail.confirmReset", { subdomain: org.subdomain }))) return;
    setActionLoading('reset');
    try {
      const response = await authFetch(`${ADMIN_API_URL}/v1/admin/organizations/${org.id}/reset-password`, { method: 'POST' });
      if (!response.ok) { const d = await response.json(); throw new Error(d.detail || t("orgDetail.errors.resetFailed")); }
      const data = await response.json();
      setCredentials(data);
      setShowPassword(true);
    } catch (err) { alert(t("orgDetail.errors.generic", { message: err.message })); }
    finally { setActionLoading(null); }
  };

  const handleResendEmail = async () => {
    if (!window.confirm(t("orgDetail.confirmResend", { email: org.admin_email }))) return;
    setActionLoading('email');
    try {
      const response = await authFetch(`${ADMIN_API_URL}/v1/admin/organizations/${org.id}/resend-welcome`, { method: 'POST' });
      if (!response.ok) { const d = await response.json(); throw new Error(d.detail || t("orgDetail.errors.emailFailed")); }
      const data = await response.json();
      setCredentials(data);
      setShowPassword(true);
      alert(data.email_sent ? t("orgDetail.alerts.emailSent") : t("orgDetail.alerts.passwordResetEmailFailed"));
    } catch (err) { alert(t("orgDetail.errors.generic", { message: err.message })); }
    finally { setActionLoading(null); }
  };

  const handleReprovision = async () => {
    if (!window.confirm(t("orgDetail.confirmReprovision", { subdomain: org.subdomain }))) return;
    setActionLoading('reprovision');
    try {
      const response = await authFetch(`${ADMIN_API_URL}/v1/admin/organizations/${org.id}/reprovision`, { method: 'POST' });
      if (!response.ok) { const d = await response.json(); throw new Error(d.detail || t("orgDetail.errors.reprovisionFailed")); }
      const data = await response.json();
      setCredentials(data);
      setShowPassword(true);
      alert(t("orgDetail.alerts.reprovisionSuccess"));
      if (onRefresh) onRefresh();
    } catch (err) { alert(t("orgDetail.errors.generic", { message: err.message })); }
    finally { setActionLoading(null); }
  };

  const handleExtendTrial = async () => {
    if (!selectedExtendDays) return;
    setActionLoading('extend');
    try {
      const response = await authFetch(
        `${ADMIN_API_URL}/v1/admin/organizations/${org.id}/extend-trial?days=${selectedExtendDays}`,
        { method: 'POST' }
      );
      if (!response.ok) { const d = await response.json(); throw new Error(d.detail || t("orgDetail.errors.extendFailed")); }
      const data = await response.json();
      setExtendResult(data);
      if (onRefresh) onRefresh();
    } catch (err) { alert(t("orgDetail.errors.generic", { message: err.message })); }
    finally { setActionLoading(null); }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const CopyBtn = ({ text, field, bg = 'amber' }) => (
    <button onClick={() => copyToClipboard(text, field)} className={`p-2 hover:bg-${bg}-100 rounded-lg transition-colors`} title={t("orgDetail.copy")}>
      {copied === field ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50">
      <div className="w-full max-w-lg h-full overflow-y-auto" style={{ backgroundColor: "var(--lm-s1)", borderLeft: "1px solid var(--lm-border)" }}>
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-foreground">{t("orgDetail.title")}</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Company header */}
          <div className="bg-primary/5 rounded-xl p-4">
            <div className="flex items-start gap-4">
              <div className="bg-blue-600 text-white p-3 rounded-xl"><Building2 className="w-8 h-8" /></div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-foreground">{org.company_name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  <a href={tenantUrl(org.subdomain)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                    {org.subdomain}.{BASE_DOMAIN} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="mt-2"><OrgStatusBadge status={org.status} /></div>
              </div>
            </div>
          </div>

          {/* Admin info */}
          <div className="space-y-3">
            <h4 className="font-semibold text-foreground flex items-center gap-2"><User className="w-4 h-4" />{t("orgDetail.adminInfo")}</h4>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-gray-400" /><span className="text-muted-foreground">{t("orgDetail.emailLabel")}</span><span className="font-medium">{org.admin_email}</span></div>
              {org.admin_phone && (
                <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-gray-400" /><span className="text-muted-foreground">{t("orgDetail.phoneLabel")}</span><span className="font-medium">{org.admin_phone}</span></div>
              )}
            </div>
          </div>

          {/* Credentials */}
          <div className="space-y-3">
            <h4 className="font-semibold text-foreground flex items-center gap-2"><Key className="w-4 h-4" />{t("orgDetail.credentials")}</h4>
            <div className="bg-amber-950/20 border border-amber-900/40 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div><span className="text-sm text-muted-foreground">{t("orgDetail.loginLabel")}</span><p className="font-mono font-medium text-foreground">admin</p></div>
                <CopyBtn text="admin" field="login" />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <span className="text-sm text-muted-foreground">{t("orgDetail.passwordLabel")}</span>
                  {org.admin_password ? (
                    <p className="font-mono font-medium text-foreground flex items-center gap-2">
                      {showPassword ? org.admin_password : '************'}
                      <button onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-muted-foreground" aria-label={showPassword ? t("adminLogin.hidePassword") : t("adminLogin.showPassword")}>
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </p>
                  ) : (
                    <p className="text-sm text-amber-400 italic">{t("orgDetail.passwordUnavailable")}</p>
                  )}
                </div>
                {org.admin_password && <CopyBtn text={org.admin_password} field="password" />}
              </div>
              <div className="pt-2 border-t border-amber-900/40">
                <span className="text-sm text-muted-foreground">{t("orgDetail.loginUrlLabel")}</span>
                <div className="flex items-center justify-between mt-1">
                  <a href={tenantUrl(org.subdomain, '/login')} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm flex items-center gap-1">
                    {tenantUrl(org.subdomain, '/login')} <ExternalLink className="w-3 h-3" />
                  </a>
                  <CopyBtn text={tenantUrl(org.subdomain, '/login')} field="url" />
                </div>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="space-y-3">
            <h4 className="font-semibold text-foreground flex items-center gap-2"><Calendar className="w-4 h-4" />{t("orgDetail.dates")}</h4>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm"><Clock className="w-4 h-4 text-gray-400" /><span className="text-muted-foreground">{t("orgDetail.createdAt")}</span><span className="font-medium">{formatDate(org.created_at)}</span></div>
              <div className="flex items-center gap-2 text-sm"><Calendar className="w-4 h-4 text-gray-400" /><span className="text-muted-foreground">{t("orgDetail.trialEnd")}</span><span className="font-medium">{formatDate(org.trial_end_date)}</span></div>
            </div>
          </div>

          {/* Database */}
          <div className="space-y-3">
            <h4 className="font-semibold text-foreground flex items-center gap-2"><Database className="w-4 h-4" />{t("orgDetail.database")}</h4>
            <div className="bg-muted/50 rounded-lg p-4">
              <code className="text-sm text-foreground">{org.database_name || `mawered_tenant_${org.subdomain}`}</code>
            </div>
          </div>

          {/* New credentials after reset */}
          {credentials && (
            <div className="space-y-3">
              <h4 className="font-semibold text-green-400 flex items-center gap-2"><Key className="w-4 h-4" />{t("orgDetail.newCredentials")}</h4>
              <div className="bg-green-950/20 border border-green-900/40 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div><span className="text-sm text-muted-foreground">{t("orgDetail.usernameLabel")}</span><p className="font-mono font-medium">{credentials.username}</p></div>
                  <CopyBtn text={credentials.username} field="username" bg="green" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-muted-foreground">{t("orgDetail.passwordLabel")}</span>
                    <p className="font-mono font-medium flex items-center gap-2">
                      {showPassword ? credentials.new_password : '************'}
                      <button onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-muted-foreground" aria-label={showPassword ? t("adminLogin.hidePassword") : t("adminLogin.showPassword")}>
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </p>
                  </div>
                  <CopyBtn text={credentials.new_password} field="password" bg="green" />
                </div>
                <div className="pt-2 border-t border-border">
                  <a href={credentials.login_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm flex items-center gap-1">
                    {credentials.login_url} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3 pt-4 border-t border-border">
            <h4 className="font-semibold text-foreground">{t("orgDetail.actions")}</h4>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={handleResetPassword} disabled={!!actionLoading} className="flex items-center justify-center gap-2 px-4 py-3 bg-amber-950/20 text-amber-400 hover:bg-amber-950/40 rounded-lg transition-colors font-medium disabled:opacity-50">
                {actionLoading === 'reset' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                {t("orgDetail.resetPassword")}
              </button>
              <button onClick={handleResendEmail} disabled={!!actionLoading} className="flex items-center justify-center gap-2 px-4 py-3 bg-muted text-muted-foreground hover:bg-muted/80 rounded-lg transition-colors font-medium disabled:opacity-50">
                {actionLoading === 'email' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {t("orgDetail.resendEmail")}
              </button>
              {!org.admin_password && (
                <button onClick={handleReprovision} disabled={!!actionLoading} className="col-span-2 flex items-center justify-center gap-2 px-4 py-3 bg-red-950/20 text-red-400 hover:bg-red-950/40 rounded-lg transition-colors font-medium disabled:opacity-50">
                  <RefreshCw className={`w-4 h-4 ${actionLoading === 'reprovision' ? 'animate-spin' : ''}`} />
                  {t("orgDetail.reprovision")}
                </button>
              )}
              <button
                onClick={() => { setShowExtendModal(true); setExtendResult(null); setSelectedExtendDays(null); }}
                disabled={!!actionLoading}
                className="col-span-2 flex items-center justify-center gap-2 px-4 py-3 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg transition-colors font-medium disabled:opacity-50"
              >
                <CalendarPlus className="w-4 h-4" />
                {t("orgDetail.extendTrial")}
              </button>
            </div>
          </div>

          {/* Extend trial modal */}
          {showExtendModal && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
              <div className="rounded-xl w-full max-w-md mx-4 overflow-hidden" style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}>
                <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: "#5e6ad2" }}>
                  <h3 className="text-white font-bold text-lg flex items-center gap-2"><CalendarPlus className="w-5 h-5" />{t("orgDetail.extendTrial")}</h3>
                  <button onClick={() => setShowExtendModal(false)} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6 space-y-5">
                  <div className="bg-primary/10 rounded-xl p-4 text-sm text-foreground">
                    <p className="font-semibold">{org.subdomain}.{BASE_DOMAIN}</p>
                    <p className="text-muted-foreground mt-1">{t("orgDetail.currentEnd")} <strong>{formatDate(org.trial_end_date)}</strong></p>
                  </div>

                  {extendResult ? (
                    <div className="bg-green-950/20 border border-green-900/40 rounded-xl p-4 text-center">
                      <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
                      <p className="font-semibold text-foreground">{t("orgDetail.extendSuccess")}</p>
                      <p className="text-muted-foreground text-sm mt-1">{t("orgDetail.newEnd")} <strong>{formatDate(extendResult.new_trial_end_date)}</strong></p>
                      <p className="text-muted-foreground text-xs mt-1">{t("orgDetail.statusLabel", { status: extendResult.new_status })}</p>
                    </div>
                  ) : (
                    <>
                      <div>
                        <p className="text-sm font-medium text-foreground mb-3">{t("orgDetail.extendBy")}</p>
                        <div className="grid grid-cols-3 gap-3">
                          {[7, 14, 30].map((d) => (
                            <button key={d} onClick={() => setSelectedExtendDays(d)}
                              className={`py-3 rounded-lg font-semibold text-sm transition-colors ${selectedExtendDays === d ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary hover:bg-primary/20'}`}>
                              <Plus className="w-3 h-3 inline mr-1" />{t("orgDetail.days", { count: d })}
                            </button>
                          ))}
                        </div>
                      </div>
                      {selectedExtendDays && (
                        <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
                          {t("orgDetail.estimatedEnd")}{' '}
                          <strong>{formatDate(new Date(Math.max(new Date(org.trial_end_date || Date.now()), Date.now()) + selectedExtendDays * 86400000).toISOString())}</strong>
                        </div>
                      )}
                      <button onClick={handleExtendTrial} disabled={!selectedExtendDays || actionLoading === 'extend'}
                        className="w-full py-3 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: "#5e6ad2" }}>
                        {actionLoading === 'extend' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
                        {t("orgDetail.confirmExtension")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationDetailPanel;
